import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import OtpField from "../../../components/auth/OtpField";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Skeleton } from "../../../components/ui/skeleton";
import { API } from "../../../constant/api";
import { useUser } from "../../../context/user-context";
import useAuthAxios from "../../../utils/baseAxios";
import { errorMessage } from "../../../utils/errorMessage";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Enrolment for authenticator-app 2FA: scan the QR, then confirm with a TOTP. */
const GoogleAuthDialog = ({ open, onOpenChange }: Props) => {
  const axiosInstance = useAuthAxios();
  const { refetch2 } = useUser();
  const [totp, setTotp] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["get-google-qrcode"],
    queryFn: async () => {
      const response = await axiosInstance.post(API.requestQrcode, {});
      return response.data.data;
    },
    // Only fetch a QR code when the merchant is actually enrolling.
    enabled: open,
    retry: 1,
  });

  const activate = useMutation({
    mutationFn: async (code: string) => {
      const response = await axiosInstance.post(API.activateGoogleAuth, {
        totp: code,
      });
      return response.data;
    },
    onSuccess: (r) => {
      toast.success(r?.message || "Authenticator app enabled");
      refetch2();
      setTotp("");
      onOpenChange(false);
    },
    onError: (e: any) =>
      toast.error(errorMessage(e?.response?.data?.message || e?.message)),
  });

  const submit = (code: string) => {
    if (code.length < 6 || activate.isPending) return;
    activate.mutate(code);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="font-manrope mds:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-sora">Authenticator app</DialogTitle>
          <DialogDescription>
            Scan this code with Google Authenticator, then enter the 6-digit
            code it shows.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="mx-auto size-[200px] overflow-hidden rounded-xl border bg-muted">
            {isLoading ? (
              <Skeleton className="size-full" />
            ) : data?.qrcode?.image ? (
              <img
                src={data.qrcode.image}
                alt="Authenticator QR code"
                className="size-full object-contain"
              />
            ) : (
              <div className="flex size-full items-center justify-center p-4 text-center text-[13px] font-medium text-muted-foreground">
                Could not load the QR code. Close and try again.
              </div>
            )}
          </div>

          <OtpField value={totp} onChange={setTotp} onComplete={submit} />
        </div>

        <DialogFooter>
          <Button
            onClick={() => submit(totp)}
            disabled={totp.length < 6 || activate.isPending}
            className="h-[52px] w-full rounded-xl text-[15px] font-semibold"
          >
            {activate.isPending ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              "Activate"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleAuthDialog;
